/**
 * PATH: apps/quote/src/components/builder/SectionsDrawer.tsx
 * PURPOSE: Left sidebar for section navigation (Excel sheet tabs feel)
 *
 * WHY:
 * - Users need quick access to navigate large quotes with many sections
 * - Shows section totals and item counts at a glance
 * - Enables inline rename and delete without window.prompt/confirm
 *
 * FLOW:
 *   Click section → Jump to section in grid → Update selection state
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  Layers,
  MoreHorizontal,
  Edit3,
  Trash2,
  Copy,
  GripVertical,
  AlertCircle,
  Check,
  X,
} from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import { useWorkspace } from './BuilderWorkspace'
import type { QuoteSection } from '@/lib/quoteApi'

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface SectionsDrawerProps {
  sections: QuoteSection[]
  onAddSection: () => void
  onRenameSection: (sectionId: string, newTitle: string) => void
  onDeleteSection: (sectionId: string) => void
  onDuplicateSection?: (sectionId: string) => void
  onReorderSections?: (sectionIds: string[]) => void
  onScrollToSection: (sectionId: string) => void
  /** Active section ID from the workspace active sheet state */
  activeSectionId?: string | null
}

interface SectionItemProps {
  section: QuoteSection
  isSelected: boolean
  isEditing: boolean
  editValue: string
  onSelect: () => void
  onStartEdit: () => void
  onEditChange: (value: string) => void
  onEditCommit: () => void
  onEditCancel: () => void
  onDelete: () => void
  onDuplicate?: () => void
}

// -----------------------------------------------------------------------------
// Delete Confirmation Modal
// -----------------------------------------------------------------------------

function DeleteConfirmModal({
  isOpen,
  sectionTitle,
  itemCount,
  onConfirm,
  onCancel,
}: {
  isOpen: boolean
  sectionTitle: string
  itemCount: number
  onConfirm: () => void
  onCancel: () => void
}) {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onCancel}>
      <div 
        className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl max-w-sm w-full mx-4 p-5"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">Delete Section?</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              This will permanently delete <strong>"{sectionTitle}"</strong>
              {itemCount > 0 && ` and all ${itemCount} line item${itemCount !== 1 ? 's' : ''}`}.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Delete Section
          </button>
        </div>
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Section Item
// -----------------------------------------------------------------------------

function SectionItem({
  section,
  isSelected,
  isEditing,
  editValue,
  onSelect,
  onStartEdit,
  onEditChange,
  onEditCommit,
  onEditCancel,
  onDelete,
  onDuplicate,
}: SectionItemProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [showMenu, setShowMenu] = useState(false)
  
  const itemCount = section.items?.length || 0
  const sectionTotal = typeof section.section_subtotal === 'number' 
    ? section.section_subtotal 
    : parseFloat(String(section.section_subtotal || 0))

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      onEditCommit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onEditCancel()
    }
  }

  return (
    <div
      className={cn(
        'group relative flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-colors',
        isSelected
          ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800'
          : 'hover:bg-slate-50 dark:hover:bg-slate-800/50 border border-transparent'
      )}
      onClick={() => !isEditing && onSelect()}
    >
      {/* Drag handle (for future reordering) */}
      <div className="opacity-0 group-hover:opacity-40 cursor-grab">
        <GripVertical className="w-4 h-4 text-slate-400" />
      </div>

      {/* Section icon */}
      <div className={cn(
        'p-1.5 rounded-md',
        isSelected 
          ? 'bg-blue-100 dark:bg-blue-900/50' 
          : 'bg-slate-100 dark:bg-slate-800'
      )}>
        <Layers className={cn(
          'w-4 h-4',
          isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'
        )} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => onEditChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={onEditCommit}
            className="w-full px-2 py-1 text-sm font-medium bg-white dark:bg-slate-800 border border-blue-400 rounded outline-none"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <>
            <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
              {section.title}
            </p>
            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <span>{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
              <span className="text-slate-300 dark:text-slate-600">•</span>
              <span className="font-medium text-slate-700 dark:text-slate-300">
                {formatCurrency(sectionTotal)}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Actions menu */}
      {!isEditing && (
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation()
              setShowMenu(!showMenu)
            }}
            className="p-1.5 rounded-md opacity-0 group-hover:opacity-100 hover:bg-slate-200 dark:hover:bg-slate-700 transition-opacity"
          >
            <MoreHorizontal className="w-4 h-4 text-slate-500" />
          </button>

          {showMenu && (
            <>
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setShowMenu(false)} 
              />
              <div className="absolute right-0 top-full mt-1 z-20 w-36 py-1 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowMenu(false)
                    onStartEdit()
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  <Edit3 className="w-4 h-4" />
                  Rename
                </button>
                {onDuplicate && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowMenu(false)
                      onDuplicate()
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                  >
                    <Copy className="w-4 h-4" />
                    Duplicate
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowMenu(false)
                    onDelete()
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Edit actions */}
      {isEditing && (
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation()
              onEditCommit()
            }}
            className="p-1.5 rounded-md bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900"
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onEditCancel()
            }}
            className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  )
}

// -----------------------------------------------------------------------------
// Main Component
// -----------------------------------------------------------------------------

export default function SectionsDrawer({
  sections,
  onAddSection,
  onRenameSection,
  onDeleteSection,
  onDuplicateSection,
  onReorderSections,
  onScrollToSection,
  activeSectionId,
}: SectionsDrawerProps) {
  const { selection, selectSection, toggleLeftPanel, isEditable } = useWorkspace()
  
  // Use activeSectionId if provided (from active sheet), otherwise fall back to selection.sectionId
  const selectedSectionId = activeSectionId ?? selection.sectionId
  
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<QuoteSection | null>(null)

  const handleStartEdit = useCallback((section: QuoteSection) => {
    setEditingId(section.id)
    setEditValue(section.title)
  }, [])

  const handleEditCommit = useCallback(() => {
    if (editingId && editValue.trim()) {
      onRenameSection(editingId, editValue.trim())
    }
    setEditingId(null)
    setEditValue('')
  }, [editingId, editValue, onRenameSection])

  const handleEditCancel = useCallback(() => {
    setEditingId(null)
    setEditValue('')
  }, [])

  const handleSelectSection = useCallback((section: QuoteSection) => {
    selectSection(section.id)
    onScrollToSection(section.id)
  }, [selectSection, onScrollToSection])

  const handleDeleteConfirm = useCallback(() => {
    if (deleteTarget) {
      onDeleteSection(deleteTarget.id)
      setDeleteTarget(null)
    }
  }, [deleteTarget, onDeleteSection])

  // Calculate totals
  const totalItems = sections.reduce((sum, s) => sum + (s.items?.length || 0), 0)
  const totalValue = sections.reduce((sum, s) => {
    const val = typeof s.section_subtotal === 'number' 
      ? s.section_subtotal 
      : parseFloat(String(s.section_subtotal || 0))
    return sum + val
  }, 0)

  return (
    <div className="h-full min-h-0 flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 p-3 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Sections</h2>
          <button
            onClick={toggleLeftPanel}
            className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
            title="Collapse panel (Alt+1)"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        </div>
        
        {/* Summary */}
        <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
          <span>{sections.length} section{sections.length !== 1 ? 's' : ''} • {totalItems} items</span>
          <span className="font-medium text-slate-700 dark:text-slate-300">
            {formatCurrency(totalValue)}
          </span>
        </div>
      </div>

      {/* Section List */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {sections.length === 0 ? (
          <div className="text-center py-8 px-4">
            <Layers className="w-10 h-10 mx-auto text-slate-300 dark:text-slate-600 mb-2" />
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
              No sections yet
            </p>
            {isEditable && (
              <button
                onClick={onAddSection}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Section
              </button>
            )}
          </div>
        ) : (
          sections.map((section) => (
            <SectionItem
              key={section.id}
              section={section}
              isSelected={selectedSectionId === section.id}
              isEditing={editingId === section.id}
              editValue={editValue}
              onSelect={() => handleSelectSection(section)}
              onStartEdit={() => handleStartEdit(section)}
              onEditChange={setEditValue}
              onEditCommit={handleEditCommit}
              onEditCancel={handleEditCancel}
              onDelete={() => setDeleteTarget(section)}
              onDuplicate={onDuplicateSection ? () => onDuplicateSection(section.id) : undefined}
            />
          ))
        )}
      </div>

      {/* Add Section Button */}
      {isEditable && sections.length > 0 && (
        <div className="flex-shrink-0 p-3 border-t border-slate-200 dark:border-slate-800">
          <button
            onClick={onAddSection}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium border-2 border-dashed border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-400 rounded-lg hover:border-blue-400 hover:text-blue-600 dark:hover:border-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Section
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        sectionTitle={deleteTarget?.title || ''}
        itemCount={deleteTarget?.items?.length || 0}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}

